import { computed, ref } from 'vue';
import { noop } from '@vueuse/core';

import type { PlsDialogState } from '@/library/cf-v1/usePleasedSupport.types';
import { useAtomic } from '@/use/useAtomic';
import { waitFor } from '@/common/utils';

import { analytics } from './EventLogger';

const syncDialog = ref<PlsDialogState>({
  open: false,
  content: 'Please wait...',
  title: '',
  actions: [],
});

/**
 * Composition-API facade around `analytics`.
 * Returns reactive computed properties and direct method proxies.
 */
export function useEventLogger() {
  const sessionId = computed(() => analytics.sessionId);

  // use this property in app.vue with some plsdialog component
  const [syncLogs] = useAtomic(
    async (params?: { endSession?: boolean; promises?: Promise<unknown>[]; timeout?: number }) => {
      const { endSession = true, promises = [], timeout } = params || {};

      syncDialog.value.open = true;
      endSession && analytics.trackSessionEndEvent();

      const analyticsPromise = (async () => {
        // Wait for all provided promises and flushLogs to finish
        await Promise.all([...promises, analytics.flushLogs()]);

        // Then execute trackSessionEnd
        endSession && (await analytics.trackSessionEnd());
      })();

      await Promise.race([
        analyticsPromise,
        ...(timeout ? [waitFor(timeout).then(() => Promise.reject(new Error('Wait dialog timeout')))] : []),
      ]).catch(noop);

      syncDialog.value.open = false;
    }
  );

  return {
    // reactive state
    syncDialog,
    sessionId,

    // core actions (all simply forwarded)
    track: analytics.track,
    trackSessionStart: analytics.trackSessionStart,
    trackSessionEndEvent: analytics.trackSessionEndEvent,
    // trackSessionEnd: analytics.trackSessionEnd,
    // trackPageEvent: analytics.trackPageEvent,
    trackClick: analytics.trackClick,
    trackAiEvent: analytics.trackAiEvent,
    startFlushingLogs: analytics.startFlushingLogs,
    restartUserSession: analytics.restartUserSession,

    // expose update for power-users
    getSessionId: () => analytics.sessionId,
    syncLogs,
    // updateUserSession: analytics.updateUserSession,
  };
}
